import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Breakpoints } from '@rd-lib/breakpoints';
import { UrlUtilsService } from '@rd/core/services/url-utils/url-utils.service';
import { WindowResizeService } from '@rd/core/services/window-resize/window-resize.service';
import { LoyaltyCurrency, LoyaltyProgram } from '@rd/types';
import { Subscription } from 'rxjs';

type ComponentRenderState = 'loading' | 'error' | 'empty' | 'content';

@Component({
  selector: 'rd-tcv2-loyalty-programs-list',
  templateUrl: './loyalty-programs-list.component.html',
  styleUrls: ['./loyalty-programs-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TcV2LoyaltyProgramsListComponent implements OnInit, OnDestroy {
  @Input() error: boolean;
  @Input() loading: boolean;
  @Input() loyaltyPrograms: LoyaltyProgram[];
  @Input() loyaltyCurrencySelected: LoyaltyCurrency;

  @HostBinding('class.rd-tcv2-loyalty-programs-list') componentClass = true;

  resizeSubscription$: Subscription;
  isMobileViewport: boolean;
  emptyLoyaltyProgramsImageUrl: string;

  constructor(
    private cdRef: ChangeDetectorRef,
    private urlUtilsService: UrlUtilsService,
    private windowResizeService: WindowResizeService,
  ) {}

  ngOnInit(): void {
    this.detectViewportSize();
    this.setupSubscriptions();
    this.emptyLoyaltyProgramsImageUrl = this.urlUtilsService.imageUrl(
      '/images/icn-noresultstcrc.svg',
    );
  }

  ngOnDestroy(): void {
    this.resizeSubscription$.unsubscribe();
  }

  getComponentRenderState(): ComponentRenderState {
    if (this.shouldShowLoading()) {
      return 'loading';
    }
    if (this.shouldShowError()) {
      return 'error';
    }
    if (this.shouldShowEmpty()) {
      return 'empty';
    }
    if (this.shouldShowLoyaltyPrograms()) {
      return 'content';
    }

    return 'error';
  }

  private shouldShowLoading(): boolean {
    return this.loading;
  }

  private shouldShowError(): boolean {
    return !!this.error;
  }

  private shouldShowEmpty(): boolean {
    return this.loyaltyPrograms && this.loyaltyPrograms.length === 0;
  }

  private shouldShowLoyaltyPrograms(): boolean {
    return !this.shouldShowLoading() && !this.shouldShowError();
  }

  private setupSubscriptions(): void {
    this.resizeSubscription$ =
      this.windowResizeService.windowResizeObservable.subscribe(() => {
        this.detectViewportSize();
        this.cdRef.detectChanges();
      });
  }

  private detectViewportSize(): void {
    this.isMobileViewport = window.innerWidth < Breakpoints.TABLET;
  }
}
